/**
 * Map one `XmlText` observe delta onto a paragraph's Slate character offsets (LG-2857).
 *
 * A `delete` is a count of absolute units, and an embed is one unit, so the count alone cannot
 * tell a character from a marker. `stream` is the pre-image that distinguishes them. Counting an
 * embed as a character would delete one extra Slate character and move the caret too far.
 * An insert on the paragraph marker belongs to the previous paragraph. A retain that does not
 * precede a character change is omitted.
 */

import { TranscriptDelta } from 'src/models/TranscriptDelta'

import { IndexedStream, ObserveDelta, indexedOpLength, rangesFromStream } from './incremental'

const slateCharsBetween = (isChar: readonly boolean[], from: number, count: number): number => {
    let total = 0
    const end = from + count

    for (let index = from; index < end; index += 1) {
        if (isChar[index]) {
            total += 1
        }
    }

    return total
}

/**
 * Slate `retain` / `insert` / `delete` for `paragraphId`, or `[]` when the delta does not change
 * that paragraph's characters. Does not mutate `stream`.
 */
export const clipObserveDeltaToParagraph = (stream: IndexedStream, paragraphId: string, delta: ObserveDelta): TranscriptDelta<string>[] => {
    const range = rangesFromStream(stream).find((item) => item.id === paragraphId)
    if (range === undefined) {
        return []
    }

    const contentEnd = range.start + range.length
    const isChar: boolean[] = []
    let offset = 0
    for (const op of stream) {
        const length = indexedOpLength(op)
        const isText = typeof op.insert === 'string'
        for (let index = offset; index < offset + length; index += 1) {
            isChar.push(isText && index > range.start && index < contentEnd)
        }
        offset += length
    }

    const edits: TranscriptDelta<string>[] = []
    let pendingRetain = 0
    let position = 0

    const flushRetain = () => {
        if (pendingRetain > 0) {
            edits.push({ retain: pendingRetain })
            pendingRetain = 0
        }
    }

    for (const op of delta) {
        if ('retain' in op) {
            pendingRetain += slateCharsBetween(isChar, position, op.retain)
            position += op.retain
            continue
        }

        if ('delete' in op) {
            const deleted = slateCharsBetween(isChar, position, op.delete)
            position += op.delete
            if (deleted > 0) {
                flushRetain()
                edits.push({ delete: deleted })
            }
            continue
        }

        if (typeof op.insert === 'string' && op.insert.length > 0 && position > range.start && position <= contentEnd) {
            flushRetain()
            edits.push({ insert: op.insert })
        }
    }

    return edits
}
